import { useState, useEffect } from 'react';
import { getClients, createClient, deleteClient, sendSingleRequest } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Users, 
  Search, 
  Send, 
  Mail,
  Calendar,
  DollarSign,
  Plus,
  Trash2,
  Zap,
  UserPlus
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sendingId, setSendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', purchase_amount: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, search, sourceFilter, statusFilter]);

  const loadClients = async () => {
    try {
      const data = await getClients();
      setClients(data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    let filtered = [...clients];
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(searchLower) ||
        c.email.toLowerCase().includes(searchLower)
      );
    }
    
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(c => c.source === sourceFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.review_status === statusFilter);
    }
    
    setFilteredClients(filtered);
  };

  const handleSendRequest = async (clientId) => {
    setSendingId(clientId);
    try {
      await sendSingleRequest(clientId);
      toast.success('Review request sent!');
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSendingId(null);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    setDeletingId(clientId);
    try {
      await deleteClient(clientId);
      toast.success('Client deleted');
      loadClients();
    } catch (error) {
      toast.error('Failed to delete client');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createClient({
        name: newClient.name,
        email: newClient.email,
        purchase_amount: newClient.purchase_amount ? parseFloat(newClient.purchase_amount) : null
      });
      toast.success('Client created successfully');
      setShowCreateModal(false);
      setNewClient({ name: '', email: '', purchase_amount: '' });
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-pending',
      sent: 'status-sent',
      reviewed: 'status-reviewed'
    };
    return <Badge className={`${styles[status] || styles.pending} border`}>{status}</Badge>;
  };

  const getSourceBadge = (source) => {
    const styles = {
      stripe: 'source-stripe',
      shopify: 'source-shopify',
      manual: 'bg-slate-100 text-slate-800 border-slate-200'
    };
    const icons = {
      stripe: '💳',
      shopify: '🛒',
      manual: '✏️'
    };
    return (
      <Badge variant="outline" className={`${styles[source] || styles.manual} border`}>
        <span className="mr-1">{icons[source] || icons.manual}</span>
        {source}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Count automated vs manual clients
  const automatedCount = clients.filter(c => c.source === 'stripe' || c.source === 'shopify').length;
  const manualCount = clients.filter(c => c.source === 'manual').length;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="h-96 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">Clients</h1>
          <p className="text-muted-foreground mt-1">
            {clients.length} total clients • {automatedCount} automated • {manualCount} manual
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="btn-animate" data-testid="create-client-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-primary/5 border-primary/20 animate-fade-in">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Automated Client Import</p>
              <p className="text-sm text-muted-foreground mt-1">
                Customers from Stripe and Shopify purchases are automatically added here. You can also manually add clients using the "Add Client" button.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 animate-fade-in">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="client-search"
          />
        </div>
        <div className="flex gap-3">
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[140px]" data-testid="source-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="stripe">Stripe</SelectItem>
              <SelectItem value="shopify">Shopify</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Clients Table */}
      <Card className="animate-fade-in" data-testid="clients-table">
        <CardContent className="p-0">
          {filteredClients.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Source</th>
                    <th>Purchase</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id} data-testid={`client-row-${client.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            client.source === 'stripe' ? 'bg-violet-100' : 
                            client.source === 'shopify' ? 'bg-green-100' : 'bg-slate-100'
                          }`}>
                            <span className={`text-sm font-semibold ${
                              client.source === 'stripe' ? 'text-violet-600' : 
                              client.source === 'shopify' ? 'text-green-600' : 'text-slate-600'
                            }`}>
                              {client.name?.charAt(0)?.toUpperCase() || 'C'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{client.name}</p>
                            <p className="text-sm text-muted-foreground">{client.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        {getSourceBadge(client.source)}
                      </td>
                      <td>
                        <div className="flex items-center gap-1 text-foreground">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          {formatCurrency(client.purchase_amount)}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 text-muted-foreground text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(client.purchase_date)}
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(client.review_status)}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {client.review_status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendRequest(client.id)}
                              disabled={sendingId === client.id}
                              className="btn-animate"
                              data-testid={`send-btn-${client.id}`}
                            >
                              {sendingId === client.id ? (
                                'Sending...'
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-1" />
                                  Send
                                </>
                              )}
                            </Button>
                          )}
                          {client.review_status === 'sent' && (
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              Sent
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClient(client.id)}
                            disabled={deletingId === client.id}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`delete-btn-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">No clients found</h3>
              <p className="empty-state-description">
                {clients.length === 0 
                  ? "Connect your Stripe or Shopify store to automatically import customers, or add clients manually."
                  : "Try adjusting your search or filters."}
              </p>
              {clients.length === 0 && (
                <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Client
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Client Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-5">
            <div className="form-group">
              <Label htmlFor="clientName">Name *</Label>
              <Input
                id="clientName"
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                placeholder="John Doe"
                required
                data-testid="client-name-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="clientEmail">Email *</Label>
              <Input
                id="clientEmail"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                placeholder="john@example.com"
                required
                data-testid="client-email-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="clientAmount">Purchase Amount (Optional)</Label>
              <Input
                id="clientAmount"
                type="number"
                step="0.01"
                value={newClient.purchase_amount}
                onChange={(e) => setNewClient({ ...newClient, purchase_amount: e.target.value })}
                placeholder="99.99"
                data-testid="client-amount-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} data-testid="save-client-btn">
                {creating ? 'Creating...' : 'Add Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
